/**
 * Utilidades de texto: FUENTE ÚNICA de normalización, iniciales y slugs.
 *
 * Absorbe las copias de mercadeo (normalizeText, normalizeStatus, subtask-type),
 * master-data, new-visit-dialog, user-context, usuarios y mapa.
 */

/** Rango de marcas de combinación (diacríticos) que deja NFD tras separar los acentos. */
export const DIACRITICS = /[̀-ͯ]/g;

/** Espacios consecutivos. */
const WHITESPACE = /\s+/g;

/** Guiones y espacios consecutivos. */
const DASHES_AND_WHITESPACE = /[-\s]+/g;

/** Todo lo que no es alfanumérico minúsculo (separador de slug). */
const NON_SLUG_CHARS = /[^a-z0-9]+/g;

/** Guiones sobrantes al inicio o al final de un slug. */
const EDGE_DASHES = /^-+|-+$/g;

/** Slug de respaldo cuando el texto no deja ningún carácter válido. */
const SLUG_FALLBACK = 'export';

/** Iniciales por defecto cuando no hay nombre. */
const INITIALS_FALLBACK = '--';

/** Normaliza para comparar: sin acentos, en minúsculas y recortado. */
export function normalizeText(value: unknown): string {
    return String(value ?? '')
        .normalize('NFD')
        .replace(DIACRITICS, '')
        .trim()
        .toLowerCase();
}

/** Clave simple de comparación: recorta y pasa a minúsculas (conserva los acentos). */
export function normalizeKey(value: unknown): string {
    return String(value ?? '').trim().toLowerCase();
}

/** Clave de estado: `normalizeText` con espacios convertidos a guion bajo. */
export function normalizeStatusKey(value: unknown): string {
    return normalizeText(value).replace(WHITESPACE, '_');
}

/** Clave de token: `normalizeText` con guiones y espacios convertidos a un solo guion bajo. */
export function normalizeTokenKey(value: unknown): string {
    return normalizeText(value).replace(DASHES_AND_WHITESPACE, '_');
}

/** Iniciales (máx. 2) de las dos primeras palabras del nombre; sin nombre devuelve `fallback`. */
export function getInitials(name: unknown, fallback: string = INITIALS_FALLBACK): string {
    const initials = String(name ?? '')
        .split(WHITESPACE)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word.charAt(0).toUpperCase())
        .join('');
    return initials || fallback;
}

/** Iniciales tomando la primera letra del nombre y del apellido; sin ninguna devuelve `fallback`. */
export function getInitialsFromParts(
    firstName: string | null | undefined,
    lastName: string | null | undefined,
    fallback: string = INITIALS_FALLBACK,
): string {
    const initials = `${firstName?.trim().charAt(0) ?? ''}${lastName?.trim().charAt(0) ?? ''}`.toUpperCase();
    return initials || fallback;
}

/** Convierte un texto a slug (a-z, 0-9 y guiones), útil para nombres de archivo. */
export function slugify(value: unknown, fallback: string = SLUG_FALLBACK): string {
    return normalizeText(value).replace(NON_SLUG_CHARS, '-').replace(EDGE_DASHES, '') || fallback;
}

/** Un trozo de texto y si coincide con lo buscado. */
export interface ParteDeTexto {
    texto: string;
    coincide: boolean;
}

/**
 * Parte un texto en lo que coincide con lo buscado y lo que no — FUENTE ÚNICA del resaltado de búsquedas.
 * Compara sin acentos ni mayúsculas (como los buscadores), pero devuelve el texto original: «Farmás» con
 * «farmas» resalta «Farmás». Sin búsqueda, o sin coincidencias, devuelve el texto entero sin resaltar.
 */
export function partirPorCoincidencia(texto: string, buscado: string): ParteDeTexto[] {
    const aguja = normalizeText(buscado);
    if (!aguja || !texto) {
        return [{ texto, coincide: false }];
    }
    // El texto normalizado letra por letra, con la posición de cada letra en el original.
    let normalizado = '';
    const origen: number[] = [];
    for (let i = 0; i < texto.length; i++) {
        const letra = texto[i].normalize('NFD').replace(DIACRITICS, '').toLowerCase();
        normalizado += letra;
        origen.push(...Array.from(letra, () => i));
    }
    const partes: ParteDeTexto[] = [];
    let desde = 0;
    let encontrado = normalizado.indexOf(aguja);
    while (encontrado !== -1) {
        const inicio = origen[encontrado];
        const fin = origen[encontrado + aguja.length - 1] + 1;
        if (inicio > desde) {
            partes.push({ texto: texto.slice(desde, inicio), coincide: false });
        }
        partes.push({ texto: texto.slice(inicio, fin), coincide: true });
        desde = fin;
        encontrado = normalizado.indexOf(aguja, encontrado + aguja.length);
    }
    if (desde < texto.length) {
        partes.push({ texto: texto.slice(desde), coincide: false });
    }
    return partes;
}
